import { apiClient } from "../../helper/api";
import {
  DocumentViewerLoadResult,
  getFileExtension,
  isOfficeExtension,
  isPdfExtension,
  loadRemoteDocumentForViewer,
} from "../../helper/documentViewerUtils";

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read file for preview."));
    reader.readAsDataURL(file);
  });

const uploadLocalFileToPreviewBlobUrl = async (
  file: File,
  token: string,
): Promise<string> => {
  const response = await apiClient(
    "/StateReport/getTemporaryDocumentPreviewUrl",
    {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
      body: JSON.stringify({
        documentName: file.name,
        blobAddress: await readFileAsDataUrl(file),
      }),
    },
  );

  if (!response) {
    throw new Error(
      "Preview service is unavailable. Confirm the internal API is running.",
    );
  }

  const previewUrl = response?.data?.previewUrl;
  if (!response?.status || !previewUrl) {
    throw new Error(
      response?.message || "Failed to prepare document preview.",
    );
  }

  return previewUrl;
};

/** Preview a local attachment — uploads to Azure, then uses the same viewer as Client Portal. */
export const loadFileForViewer = async (
  file: File,
  token: string,
): Promise<DocumentViewerLoadResult> => {
  const extension = getFileExtension(file.name);
  const mimeType = file.type?.split(";")[0].trim().toLowerCase() || "";

  if (isPdfExtension(extension) || mimeType === "application/pdf") {
    return {
      viewerUrl: URL.createObjectURL(file),
      excelWorkbook: null,
      isBlobUrl: true,
    };
  }

  if (isOfficeExtension(extension)) {
    const blobUrl = await uploadLocalFileToPreviewBlobUrl(file, token);
    return loadRemoteDocumentForViewer(blobUrl, file.name);
  }

  return {
    viewerUrl: URL.createObjectURL(file),
    excelWorkbook: null,
    isBlobUrl: true,
  };
};

export { loadRemoteDocumentForViewer };
